/**
 * Device-local wishlist: product handles stored in localStorage.
 * - <eme-wishlist-button data-handle="..."> toggles a product.
 * - [data-eme-wishlist-open] opens <eme-wishlist-drawer>, which renders saved products with the
 *   product.eme-wishlist template (the studio product card).
 */

const STORAGE_KEY = 'eme-wishlist';
const CHANGE_EVENT = 'eme:wishlist-change';

/** @returns {string[]} */
function readList() {
  try {
    const list = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

/** @param {string[]} list */
function writeList(list) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    // Storage can be unavailable (private mode); the in-page state still updates
  }
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: list }));
}

/** @param {string} handle */
function toggle(handle) {
  const list = readList();
  const index = list.indexOf(handle);
  if (index === -1) list.unshift(handle);
  else list.splice(index, 1);
  writeList(list);
}

class EmeWishlistButton extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    document.addEventListener(CHANGE_EVENT, this.#sync);
    window.addEventListener('storage', this.#sync);
    this.#sync();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.#sync);
    window.removeEventListener('storage', this.#sync);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (this.dataset.handle) toggle(this.dataset.handle);
  };

  #sync = () => {
    const saved = readList().includes(this.dataset.handle ?? '');
    this.toggleAttribute('data-saved', saved);
    this.querySelector('button')?.setAttribute('aria-pressed', String(saved));
  };
}

class EmeWishlistDrawer extends HTMLElement {
  connectedCallback() {
    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener(CHANGE_EVENT, this.#onChange);
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener(CHANGE_EVENT, this.#onChange);
  }

  get #dialog() {
    return /** @type {HTMLDialogElement | null} */ (this.querySelector('dialog'));
  }

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    const opener = /** @type {HTMLElement} */ (event.target).closest('[data-eme-wishlist-open]');
    if (!opener) return;
    event.preventDefault();
    this.open();
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.closest('[data-eme-wishlist-close]') || target === this.#dialog) {
      this.#dialog?.close();
    }
  };

  /** Removes cards for products taken off the list while the drawer is open */
  #onChange = () => {
    const list = readList();
    for (const item of this.querySelectorAll('[data-wishlist-item]')) {
      if (!list.includes(/** @type {HTMLElement} */ (item).dataset.wishlistItem ?? '')) item.remove();
    }
    this.#updateCount(list.length);
  };

  /** @param {number} count */
  #updateCount(count) {
    const countEl = this.querySelector('[data-wishlist-count]');
    if (countEl) countEl.textContent = count ? `(${count})` : '';
    const empty = /** @type {HTMLElement | null} */ (this.querySelector('[data-wishlist-empty]'));
    if (empty) empty.hidden = count > 0;
  }

  async open() {
    const dialog = this.#dialog;
    const grid = this.querySelector('[data-wishlist-grid]');
    if (!dialog || !grid) return;

    dialog.showModal();

    const list = readList();
    this.#updateCount(list.length);
    grid.replaceChildren();
    if (list.length === 0) return;

    this.setAttribute('aria-busy', 'true');
    const root = window.Shopify?.routes?.root || '/';

    const results = await Promise.all(
      list.map(async (handle) => {
        try {
          const response = await fetch(`${root}products/${encodeURIComponent(handle)}?view=eme-wishlist`);
          if (!response.ok) return { handle, html: null };
          return { handle, html: await response.text() };
        } catch {
          return { handle, html: null };
        }
      })
    );

    for (const { handle, html } of results) {
      if (!html) continue;
      const item = document.createElement('div');
      item.className = 'eme-wishlist__item';
      item.dataset.wishlistItem = handle;
      item.innerHTML = html;
      grid.append(item);
    }

    // Drop products that no longer exist
    const missing = results.filter((r) => !r.html).map((r) => r.handle);
    if (missing.length) writeList(readList().filter((h) => !missing.includes(h)));

    this.removeAttribute('aria-busy');
  }
}

if (!customElements.get('eme-wishlist-button')) {
  customElements.define('eme-wishlist-button', EmeWishlistButton);
}

if (!customElements.get('eme-wishlist-drawer')) {
  customElements.define('eme-wishlist-drawer', EmeWishlistDrawer);
}
